import { UploadOutlined } from '@ant-design/icons';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { getSimpleWidgetReference } from '@framework/engine/schema/expressions/references/builder/getSimpleWidgetReference.ts';
import { type IRuleType } from '@framework/engine/schema/rules/IRuleType.ts';
import { FileTypeRule } from '@framework/engine/schema/rules/repository/file-type/FileType.rule.ts';
import { MaxDimensionRule } from '@framework/engine/schema/rules/repository/max-dimension/MaxDimension.rule.ts';
import { NeedSignRule } from '@framework/engine/schema/rules/repository/need-sign/NeedSign.rule.ts';
import { RequiredRule } from '@framework/engine/schema/rules/repository/required/Required.rule.ts';
import { tran } from '@framework/engine/schema/translations/schema/TranslationSchema.ts';
import { type IWidgetType } from '@framework/engine/schema/widgets/IWidgetType.ts';
import {
  WidgetCategory,
  WidgetMainType,
} from '@framework/engine/schema/widgets/Widget.ts';
import { createUplaodWidgetFormSchema } from './createUploadWidgetFormSchema.ts';
import { hydrateUploadWidget } from './hydrateUploadWidget.ts';
import { type IUploadWidget, WidgetUploadType } from './IUploadWidget.ts';
import { uploadInputConfigSchema } from './UploadInputConfigSchema.ts';
import { UploadWidgetComponent } from './UploadWidgetComponent.tsx';
import { UploadWidgetConfigComponent } from './UploadWidgetConfigComponent.tsx';
import { UploadWidgetPreview } from './UploadWidgetPreview.tsx';
import { UploadWidgetReadonlyComponent } from './UploadWidgetReadonlyComponent.tsx';

declare module '@framework/engine/schema/widgets/Widget.ts' {
  export enum WidgetType {
    Upload = 'upload',
  }
}

export function publicDataUploadWidgetName(path: string) {
  return `${path}@publicData`;
}

/**
 * Costruisce la chiave del campo che contiene il nome del file data
 * la chiave dell'id. Utilizzato per gestire i default da reference.
 */
export function getContextFileNameField(fileIdField: string) {
  return `${fileIdField}@filename`;
}

export const UploadWidgetType: IWidgetType<IUploadWidget> = {
  type: WidgetUploadType,
  mainType: WidgetMainType.Input,
  category: WidgetCategory.Input,
  name: 'Campo Upload',
  icon: <UploadOutlined />,
  supportedSectionTypes: ['FRONTOFFICE'],
  hasValue: true,
  hydrate: hydrateUploadWidget,
  getReference: getSimpleWidgetReference(Datatype.File),
  formComponent: UploadWidgetComponent,
  createFormSchema: createUplaodWidgetFormSchema,
  previewComponent: UploadWidgetPreview,
  configValidator: uploadInputConfigSchema,
  configComponent: UploadWidgetConfigComponent,
  readonlyComponent: UploadWidgetReadonlyComponent,
  translationSchema: tran.widget({
    label: tran.rte(),
    helpText: tran.rte(),
    title: tran.rte(),
    publicUploadTitle: tran.rte(),
    publicUploadLabel: tran.rte(),
    publicHelpText: tran.rte(),
    rules: tran.rules(),
  }),
  // Facciamo un cast a IRuleType perché MaxDimensionRule non ha tutti i campi
  // opzionali (vedi .default() su value), richiesti dal tipo. Col cast evitiamo
  // errori di TS.
  rules: [
    RequiredRule,
    NeedSignRule,
    MaxDimensionRule,
    FileTypeRule,
  ] as IRuleType[],
  getSiblingKeys: (code: string) => [
    publicDataUploadWidgetName(code),
    getContextFileNameField(publicDataUploadWidgetName(code)),
    getContextFileNameField(code),
  ],
  aliases: ['upload', 'allegato', 'file'],
};
